/**
 * Everything that needs an *instant* for a dive center: when its day starts,
 * what "now" is there, and when an activity actually begins.
 *
 * Booking days themselves are calendar days and carry no timezone (see
 * app/utils/calendarDay.ts). This module is only for the cases where a real
 * moment is required: minimum booking lead time, the center's "today",
 * reminders and deferred charges.
 *
 * No dependency: Intl carries the IANA database in Node and in browsers.
 */

/** Centers without a zone fall back to UTC, which every caller already handled. */
export const DEFAULT_CENTER_TIME_ZONE = "UTC";

export function isValidTimeZone(zone: string | null | undefined): boolean {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export function resolveCenterTimeZone(zone?: string | null): string {
  return isValidTimeZone(zone) ? (zone as string) : DEFAULT_CENTER_TIME_ZONE;
}

/** Offset of `zone` at `instant`, in minutes east of UTC. */
function offsetMinutes(instant: Date, zone: string): number {
  // "en-CA" gives YYYY-MM-DD, so the parts reassemble in a sortable order.
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");
  // Hour 24 appears at midnight in some locales; normalize it to 0.
  const hour = get("hour") % 24;
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    hour,
    get("minute"),
    get("second")
  );
  return (asUtc - instant.getTime()) / 60000;
}

/**
 * The instant at which a wall-clock time happens in `zone`.
 * Resolved twice so the offset is the one in force at the target moment, which
 * matters around daylight saving changes.
 */
export function zonedTimeToInstant(
  dayKey: string,
  timeOfDay: string,
  zone?: string | null
): Date {
  const tz = resolveCenterTimeZone(zone);
  const [year, month, day] = dayKey.split("-").map(Number);
  const [hours, minutes] = (/^(\d{1,2}):(\d{2})/.exec(timeOfDay || "0:00") || [])
    .slice(1)
    .map(Number);
  const naive = Date.UTC(year, month - 1, day, hours || 0, minutes || 0, 0, 0);
  let instant = new Date(naive - offsetMinutes(new Date(naive), tz) * 60000);
  instant = new Date(naive - offsetMinutes(instant, tz) * 60000);
  return instant;
}

/** Day key ("YYYY-MM-DD") that it currently is at the dive center. */
export function todayAtCenter(zone?: string | null, now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: resolveCenterTimeZone(zone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/**
 * When the activity of `dayKey` starts, as a real instant. Without a start
 * time it is midnight at the center.
 */
export function activityStartInstant(
  dayKey: string,
  startTime?: string | null,
  zone?: string | null
): Date {
  return zonedTimeToInstant(dayKey, startTime || "00:00", zone);
}

/** Hours after the activity before the dive center is paid. */
export const PAYOUT_DELAY_HOURS = 48;

/**
 * When a dive center is paid: 48 hours after the activity, counted in the
 * center's own time.
 *
 * It used to be `addDays(lastBookedDate, 2)`. Booking dates are calendar days
 * stored at midnight UTC, so that fired at 00:00 UTC on day+2 — anywhere from
 * about 34 to 50 hours after the activity depending on the start time and the
 * country, and never the 48 hours the payout policy promises.
 *
 * Without a start time (a liveaboard trip's end date) it counts from midnight
 * at the center. Without a usable timezone it falls back to UTC, which is the
 * old behaviour, so nothing regresses for a center that has no zone set.
 */
export function payoutDueAt(params: {
  /** Last booked day, as a Date (calendar day) or a "YYYY-MM-DD" key. */
  lastDay: Date | string;
  /** "HH:MM" local to the center, when the activity has one. */
  startTime?: string | null;
  timeZone?: string | null;
  delayHours?: number;
}): Date {
  const { lastDay, startTime, timeZone, delayHours = PAYOUT_DELAY_HOURS } = params;
  const dayKey =
    typeof lastDay === "string"
      ? lastDay.slice(0, 10)
      : new Date(lastDay).toISOString().slice(0, 10);
  const start = activityStartInstant(dayKey, startTime, timeZone);
  return new Date(start.getTime() + delayHours * 3600000);
}
